import request from 'sb-request'
import { getState, reducers } from 'redux/core'
import { getFeatureToggle } from 'helpers'
import { constants, getAbValue, getGqlQuery, getMainPageLink } from 'common/helpers'
import { modifyProduct } from 'common/helpers/modifiers/modifyResponse'
import actions from 'redux/actions'


const reset = () => reducers.view.productPage.reset()

const redirectToMainPage = () => {
  const state = getState()
  const link = getMainPageLink({ state })

  actions.models.router.replace(link)
}

// TODO replace rest/subscription/perfume/slug and rest/product?slug= with graphql
const fetch = (productSlug) => {
  const isProductPageGQLEnabled = getFeatureToggle(constants.gqlFeatures.productPageGQL)
  const isProductSectionLabResultEnabled = getFeatureToggle(constants.features.productSectionLabResults)

  if (isProductPageGQLEnabled) {
    const productQuery = getGqlQuery('product', { slug: String(productSlug) })

    const baseMediaFields = `
      bigImage
      preview
      thumb
      type
    `

    return request.post('graphql?name=product', {
      body: {
        query: `{
          ${productQuery} {
            id
            name
            brand
            category
            description
            sex
            img
            type
            typeGroup
            retailPrice
            upchargePrice
            totalPurchases
            url
            labels
            media {
              ${baseMediaFields}
            }
            heroMedia {
              ${baseMediaFields}
            }
            makeupMedia {
              ${baseMediaFields}
              key
              name
            }
            descriptionSections {
              id
              title
              text
              ${isProductSectionLabResultEnabled ? 'data' : ''}
            }
            notes {
              id
              img
              name
            }
            tagTypes {
              id
              name
              type
              topTagId
              topTagImage
              topTagName
              voteCount
              tags {
                id
                name
                image
                voteCount
              }
            }
            tradingItems {
              id
              tradingId: id
              type
              price
              inStock
              volume {
                volume
                unit
              }
              image
              productGroup
              color
              colorName
              priceWithDiscount
            }
            visibility
            meta {
              title
              description
            }
            ratings {
              avgRate
              count
              distribution
            }
            pageTitle
            isLimitedEdition
            limitedEditionVolumeCustomText
            limitedEditionProductsRemainingCount
            isSubscribedToStockNotification
          }
        }`,
      },
      withErrorModal: false,
      modifyResult: ({ product }) => {
        // backend returns null and 404 if product has 'visibility' === 'INVISIBLE' or doesn't exists
        // backend may return it with errors so we need double check in modifyResult and reject handlers
        if (!product) {
          redirectToMainPage()

          return {}
        }

        const { tradingItems, ...rest } = product
        const activeVolumeIndex = getActiveVolumeIndex(tradingItems)

        return modifyProduct({ ...rest, tradingItems, activeVolumeIndex, isProductPageGQLEnabled })
      },
    })
      .then(
        (result) => {
          reducers.view.productPage.set(result)

          return result
        },
        () => {
          // backend returns null and 404 if product has 'visibility' === 'INVISIBLE' or doesn't exists
          // backend may return it with errors so we need double check in modifyResult and reject handlers
          redirectToMainPage()

          return Promise.reject()
        }
      )
  }

  return request.get(`rest/product?slug=${productSlug}&detailed=true`, {
    withErrorModal: false,
    modifyResult: ({ items, ...rest }) => {
      const activeVolumeIndex = getActiveVolumeIndex(items)

      return modifyProduct({ ...rest, items, activeVolumeIndex })
    },
  })
    .then(
      (result) => {
        reducers.view.productPage.set(result)

        return result
      },
      ({ statusCode }) => {
        // backend returns null and 404 if product has 'visibility' === 'INVISIBLE' or doesn't exists
        if (statusCode === 404) {
          redirectToMainPage()
        }

        return Promise.reject()
      }
    )
}

/**
 * @param {number} productId
 */
const fetchRecommendations = (productId) => {
  const isProductPageGQLEnabled = getFeatureToggle(constants.gqlFeatures.productPageGQL)

  const modifyResult = (response = {}) => {
    response.categories = response.categories.map(({ name, ...rest }, index) => {
      let metadata
      let snowplowMetadata

      if (name === 'People who wear this also liked') {
        metadata = {
          placement: 'product page',
          tab: 'similar',
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'similar',
        }
      }
      else if (/^More (scents|products) from.*/.test(name)) {
        metadata = {
          placement: 'product page',
          tab: 'brand',
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'brand',
        }
      }
      else if (name === 'Extra cases') {
        metadata = {
          placement: 'product page',
          tab: 'cases',
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'cases',
        }
      }
      else if (name === 'Best if used with') {
        metadata = {
          placement: 'product page',
          tab: 'best_used',
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'best_used',
        }
      }
      else if (name === 'Products we love') {
        metadata = {
          placement: 'product page',
          tab: 'we love',
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: 'we love',
        }
      }
      else if (/^More.*scents$/.test(name)) {
        const isNote  = index === 2
        const isType  = index === 3
        const tab     = isNote ? 'scent note' : isType ? 'scent type' : ''

        metadata = {
          placement: 'product page',
          tab,
        }
        snowplowMetadata = {
          placement: 'recommendations',
          sectionTab: tab,
        }
      }

      return {
        title: name,
        metadata,
        snowplowMetadata,
        ...rest,
      }
    })

    return response
  }

  if (isProductPageGQLEnabled) {
    // TODO ask @backend change "getRecommendations" to "recommendations"
    const recommendationsQuery = getGqlQuery('getRecommendations', { productId })

    return request.post('graphql?name=recommendations', {
      body: {
        query: `{
          categories: ${recommendationsQuery} {
            name
            version
            provider
            products {
              id
              img
              url
              brand
              name
              category
              price
              sex
              type
              popularIn
              totalPurchased
              totalPurchasedInState
              labels
              rating
              upchargePrice
            }
          }
        }`,
      },
      modifyResult,
    })
      .then(({ categories }) => {
        reducers.view.productPage.setRecommendations(categories)
        return categories
      })
  }

  return request.get(`rest/product/${productId}/recommendations`, {
    modifyResult,
  })
    .then(({ categories }) => {
      reducers.view.productPage.setRecommendations(categories)
      return categories
    })
}

const setActiveVolumeIndex = (index) => reducers.view.productPage.setActiveVolumeIndex(index)

const getActiveVolume = (state) => {
  const { view: { productPage: { volumes, activeVolumeIndex } } } = state

  return volumes[activeVolumeIndex]
}

const getActiveVolumeIndex = (volumes) => {
  const {
    routing: {
      location: { hash },
    },
    view: {
      productPage: { type },
    },
  } = getState()

  const inStockVolumes = volumes.filter(({ inStock }) => inStock)

  if (hash === '#ecommerce' && inStockVolumes.length > 1) {
    return type === 'Perfume' ? 1 : inStockVolumes.length - 1
  }

  return 0
}

/**
 *
 * @param {number} productId
 */
const fetchReviews = (productId) => {
  const isProductPageGQLEnabled = getFeatureToggle(constants.gqlFeatures.productPageGQL)

  const state = getState()
  const { view: { productPage: { reviewsData: { paginationCursor, sortBy, selectedRating } } } } = state

  const globals = window.__globals__
  const withExpertReview  = (
    !paginationCursor
    && getAbValue(constants.abTests.productExpertReviews) === 'b'
  )

  const isGoogleBot = (
    globals.device.isBot
    && getFeatureToggle(constants.features.isGoogleBot)
  )

  const count = isGoogleBot ? -1 : 20

  reducers.view.productPage.setReviewsFetching(true)

  if (isProductPageGQLEnabled) {
    const productQuery = getGqlQuery('product', { slug: String(productId) })

    const helpfulQuery = `helpful {
      positive
      negative
      isUseful
    }`

    const authorQuery = `user {
      id
      firstName
      lastName
      gender
      avatar
      url
      stats {
        shippedCount: orders
        reviewsTotal: reviews
        likeTotal: voteUpTotal
        dislikeTotal: voteDownTotal
      }
    }`

    const repliesQuery = `replies {
      id
      text
      date
      ${helpfulQuery}
      ${authorQuery}
    }`

    const userReviewQuery = `userReview {
      id
      title
      text
      date
      rating
      ageCategory
      skinType
      tags {
        id
        img
        name
      }
      ${helpfulQuery}
      ${repliesQuery}
    }`

    const expretReviewQuery = `expertReview {
      id
      date
      name
      upvoteCount
      downvoteCount
      text
      ${helpfulQuery}
    }`

    const reviewsQuery = `${getGqlQuery('reviews', {
      offset: paginationCursor,
      limit: count,
      sort: sortBy,
      rating: selectedRating,
      excludeComplainedReviews: withExpertReview,
    })} {
      id
      title
      text
      date
      ageCategory
      rating
      skinType
      tags {
        id
        img
        name
      }
      ${helpfulQuery}
      ${authorQuery}
      ${repliesQuery}
    }`

    return request.post('graphql?name=productReviews', {
      body: {
        query: `{
          ${productQuery} {
            ratings {
              average: avgRate
              reviewCount: count
              reviews: distribution
            }
            ${userReviewQuery}
            ${expretReviewQuery}
            ${reviewsQuery}
          }
        }`,
      },
      // TODO: rework Reviews component – replace likeCount, dislikeCount with helpful object
      // and after remove this modifyResult
      modifyResult: (result) => {
        const { product: { ratings, userReview, expertReview, reviews } } = result

        const getObjectWithInjectedHelpful = (obj) => {
          const { helpful: { positive: likeCount, negative: dislikeCount, isUseful } } = obj

          return {
            ...obj,
            likeCount,
            dislikeCount,
            isUseful,
          }
        }

        const getUserReviewWithInjectedHelpful = (userReview) => ({
          ...getObjectWithInjectedHelpful(userReview),
          replies: userReview.replies.map((reply) => getObjectWithInjectedHelpful(reply)),
        })

        return {
          rating: ratings,
          userReview: userReview ? getUserReviewWithInjectedHelpful(userReview) : userReview,
          expertReview: expertReview ? getObjectWithInjectedHelpful(expertReview) : expertReview,
          reviews: reviews ? reviews.map((review) => getUserReviewWithInjectedHelpful(review)) : reviews,
        }
      },
    })
      .then((result) => {
        reducers.view.productPage.setReviews(result)

        return result
      })
  }

  return request.get(`rest/product/${productId}/reviews`, {
    query: {
      start: paginationCursor,
      count,
      sortBy,
      filterByRating: selectedRating,
      withExpertReview,
    },
  })
    .then((result) => {
      reducers.view.productPage.setReviews(result)

      return result
    })
}

const voteForReview = (productId, reviewId, replyId, isUseful) => {
  const isProductPageGQLEnabled = getFeatureToggle(constants.gqlFeatures.productPageGQL)
  const isExpertReview          = reviewId === 'expertReview'

  if (isExpertReview) {
    reducers.view.productPage.setExpertReviewVoteFor(isUseful)
  }
  else {
    reducers.view.productPage.setReviewVoteFor({ reviewId, replyId, isUseful })
  }

  if (isProductPageGQLEnabled) {
    let mutation
    let input

    if (replyId) {
      mutation = 'rateReviewReply'
      input = { isUseful, replyId }
    }
    else if (isExpertReview) {
      mutation = 'rateExpertReview'
      input = { isUseful }
    }
    else {
      mutation = 'rateReview'
      input = { isUseful, reviewId }
    }

    return request.post(`graphql?name=${mutation}`, {
      body: {
        query: `
          mutation {
            ${getGqlQuery(mutation, { input })} {
              result
            }
          }
        `,
      },
      withErrorModal: false,
    })
  }

  const baseUrl = `rest/product/${productId}/reviews/${reviewId}`
  const url     = replyId ? `${baseUrl}/replies/${replyId}/rate` : `${baseUrl}/rate`

  return request.post(url, {
    body: {
      isUseful,
    },
    withErrorModal: false,
  })
}

const setPaginationCursor = (paginationCursor) => {
  const { view: { productPage: { id } } } = getState()

  reducers.view.productPage.setPaginationCursor(paginationCursor)

  return fetchReviews(id)
}

const setSortBy = (sortBy) => {
  reducers.view.productPage.setSortBy(sortBy)
  setPaginationCursor(0)
}

const setSelectedRating = (selectedRating) => {
  reducers.view.productPage.setSelectedRating(selectedRating)
  setPaginationCursor(0)
}

const setReplyFormShown = (reviewId) => reducers.view.productPage.setReplyFormShown(reviewId)

const sendReply = (reviewId, message) => {
  const { view: { productPage: { id: productId } } } = getState()

  const isProductPageGQLEnabled = getFeatureToggle(constants.gqlFeatures.productPageGQL)

  if (isProductPageGQLEnabled) {
    const input = {
      reviewId,
      message,
    }

    return request.post('graphql?name=replyReview', {
      body: {
        query: `
          mutation {
            ${getGqlQuery('replyReview', { input })} {
              result
            }
          }
        `,
      },
    })
      .then(() => fetchReviews(productId))
  }

  return request.post(`rest/product/${productId}/reviews/${reviewId}/replies`, {
    body: {
      message,
    },
  })
    .then(() => fetchReviews(productId))
}

const handleStockNotificationSubscriptionSuccess = () =>
  reducers.view.productPage.setIsSubscribedToStockNotification(true)

const setIsLandingVersionEnabled = (value) => reducers.view.productPage.setIsLandingVersionEnabled(value)

const setSource = (value) => reducers.view.productPage.setSource(value)

const setOldPathname = (value) => reducers.view.productPage.setOldPathname(value)


export default {
  reset,
  fetch,
  fetchRecommendations,
  setActiveVolumeIndex,
  getActiveVolume,
  fetchReviews,
  voteForReview,
  setPaginationCursor,
  setSortBy,
  setSelectedRating,
  setReplyFormShown,
  sendReply,
  handleStockNotificationSubscriptionSuccess,
  setIsLandingVersionEnabled,
  setSource,
  setOldPathname,
}
